import { useMemo, ReactElement } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { defaultItems, defaultSubtitle, defaultTitle } from '@pages/Forbidden/Forbidden.constants';
import { arraysOverlap } from '@utils/arrays';
import { FullscreenLoading } from '@components/Loading';
import { useStore } from '@store/GlobalStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchSystemMaintenanceEvents } from '@services/systemManagement';
import { FallbackMaintenance } from '@components/errorBoundary/FallbackMaintenance';
import { Forbidden } from '../pages';
import { EnablePaymentPortal, useApiEnvStore } from '@utils/api-client';
import { shallow } from 'zustand/shallow';
import { DebugModeControls } from '@components/debugModeControls/debugModeControls';

type Props = {
  children: ReactElement;
  routeSpecificRoles: string[];
  outlet?: boolean;
};

export const RoleGuard = ({ children, routeSpecificRoles, outlet }: Props) => {
  const { oktaAuth } = useOktaAuth();
  const [roles, userAuth] = useStore((state) => [state.roles, state.userAuth, state.currentUser], shallow);
  const navigate = useNavigate();
  const debugMode = useApiEnvStore((state) => state.debugMode);

  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isPaymentPortal = location.pathname.includes('/payments');
  const hasAccess = arraysOverlap(routeSpecificRoles, roles);
  const timeNow = useMemo(() => new Date().toISOString(), []);
  const { data: maintenanceEvents } = useSearchSystemMaintenanceEvents({
    activeAt: timeNow,
  });
  const activeMaintenanceEvent = maintenanceEvents?.items[0];

  if (userAuth.isLoading) {
    return <FullscreenLoading />;
  }

  if (roles.length === 0 || userAuth.isError || (isPaymentPortal && !EnablePaymentPortal)) {
    return (
      <>
        <Forbidden
          fullScreen
          title='Something went wrong.'
          subtitle={[
            'There has been an issue with your user login.',
            'Please contact LIO support at service@lioinsurance.com',
            'or call 877-546-7155 M-F between 8:30am - 8:00pm ET.',
          ]}
          primaryBtnText='Log out'
          primaryBtnAction={async () => {
            await oktaAuth.signOut();
          }}
        />
        {debugMode && <DebugModeControls />}
      </>
    );
  }

  if (hasAccess) {
    if (activeMaintenanceEvent && !userAuth.isAdmin && !userAuth.isPortalAdmin) {
      return <FallbackMaintenance activeEvent={activeMaintenanceEvent} />;
    }
    return children;
  }

  if (outlet && !hasAccess) {
    navigate('/not-found');
  }

  return (
    <Forbidden
      title={defaultTitle}
      subtitle={defaultSubtitle}
      items={defaultItems}
      primaryBtnText={isHomePage || userAuth.isCustomerUser ? undefined : 'Continue to your Homepage'}
      primaryBtnRedirect={isHomePage ? undefined : '/'}
      secondaryBtnText='Log out'
      secondaryBtnAction={async () => {
        await oktaAuth.signOut();
      }}
    />
  );
};
